package com.example.positionahead.domain

import com.example.positionahead.model.AssetClass
import com.example.positionahead.model.Position
import com.example.positionahead.model.PositionLot
import com.example.positionahead.model.positionWithSingleLot
import kotlin.random.Random

object CsvImporter {

    data class ImportResult(
        val positions: List<Position>,
        val errors: List<String>,
    )

    fun parse(csv: String): ImportResult {
        val lines = csv.lines()
            .map { it.trim() }
            .filter { it.isNotEmpty() && !it.startsWith("#") }

        if (lines.isEmpty()) {
            return ImportResult(emptyList(), listOf("No data found."))
        }

        val positions = mutableListOf<Position>()
        val errors = mutableListOf<String>()

        val firstLine = lines.first()
        val headers = parseHeaders(firstLine)
        val hasHeader = headers.containsKey("symbol") ||
            headers.containsKey("ticker") ||
            (headers.containsKey("price") && headers.containsKey("quantity"))

        val startIndex = if (hasHeader) 1 else 0

        for (i in startIndex until lines.size) {
            val parts = splitCsvLine(lines[i])
            if (parts.size < 3) {
                errors += "Line ${i + 1}: expected at least symbol, shares, and cost basis"
                continue
            }

            val parsed = if (hasHeader) parseWithHeaders(headers, parts, i + 1)
            else parsePositional(parts, i + 1)

            when (parsed) {
                is ParsedRow.Error -> errors += parsed.message
                is ParsedRow.Ok -> positions += positionWithSingleLot(
                    id = newId(),
                    symbol = parsed.symbol,
                    shares = parsed.shares,
                    costBasisPerShare = parsed.costBasis,
                    assetClass = parsed.assetClass,
                    purchaseDate = parsed.purchaseDate,
                )
            }
        }

        return ImportResult(PositionMerger.mergeBySymbol(positions), errors)
    }

    private sealed class ParsedRow {
        data class Ok(
            val symbol: String,
            val shares: Double,
            val costBasis: Double,
            val assetClass: AssetClass,
            val purchaseDate: String? = null,
        ) : ParsedRow()

        data class Error(val message: String) : ParsedRow()
    }

    private fun parseWithHeaders(headers: Map<String, Int>, parts: List<String>, lineNumber: Int): ParsedRow {
        val symbolIndex = columnIndex(headers, "symbol", "ticker") ?: 0
        val sharesIndex = columnIndex(headers, "quantity", "shares", "qty") ?: 2
        val costIndex = columnIndex(headers, "price", "costbasis", "cost", "purchaseprice") ?: 1
        val assetClassIndex = columnIndex(headers, "assetclass", "class", "type")
        val dateIndex = columnIndex(headers, "date", "purchasedate", "tradedate")

        val symbol = parts.getOrNull(symbolIndex)?.trim()?.uppercase().orEmpty()
        val shares = parts.getOrNull(sharesIndex)?.trim()?.toDoubleOrNull()
        val costBasis = parts.getOrNull(costIndex)?.trim()?.toDoubleOrNull()
        val purchaseDate = dateIndex?.let { parts.getOrNull(it)?.trim()?.takeIf { value -> value.isNotBlank() } }
        val assetClass = assetClassIndex?.let { parts.getOrNull(it)?.let(::parseAssetClass) }
            ?: guessAssetClass(symbol)

        return validate(symbol, shares, costBasis, assetClass, lineNumber, purchaseDate)
    }

    private fun parsePositional(parts: List<String>, lineNumber: Int): ParsedRow {
        val symbol = parts[0].trim().uppercase()
        val second = parts[1].trim().toDoubleOrNull()
        val third = parts[2].trim().toDoubleOrNull()

        val (shares, costBasis) = when {
            second == null || third == null ->
                return ParsedRow.Error("Line $lineNumber: could not parse numeric columns")
            second > third -> third to second
            else -> second to third
        }

        val fourth = parts.getOrNull(3)?.trim()
        val purchaseDate = fourth?.takeIf { looksLikeDate(it) }
        val assetClass = when {
            fourth != null && !looksLikeDate(fourth) -> parseAssetClass(fourth)
            else -> guessAssetClass(symbol)
        }

        return validate(symbol, shares, costBasis, assetClass, lineNumber, purchaseDate)
    }

    private fun looksLikeDate(value: String): Boolean =
        value.contains("/") || value.contains("-")

    private fun validate(
        symbol: String,
        shares: Double?,
        costBasis: Double?,
        assetClass: AssetClass,
        lineNumber: Int,
        purchaseDate: String? = null,
    ): ParsedRow = when {
        symbol.isBlank() -> ParsedRow.Error("Line $lineNumber: missing symbol")
        shares == null || shares <= 0 -> ParsedRow.Error("Line $lineNumber: invalid shares/quantity")
        costBasis == null || costBasis < 0 -> ParsedRow.Error("Line $lineNumber: invalid price/cost basis")
        else -> ParsedRow.Ok(symbol, shares, costBasis, assetClass, purchaseDate)
    }

    private fun parseHeaders(line: String): Map<String, Int> {
        val columns = splitCsvLine(line)
        val map = mutableMapOf<String, Int>()
        columns.forEachIndexed { index, column ->
            val key = normalizeHeader(column)
            if (key.isNotEmpty()) {
                map[key] = index
            }
        }
        return map
    }

    private fun normalizeHeader(value: String): String =
        value.trim().lowercase().replace(" ", "").replace("_", "")

    private fun columnIndex(headers: Map<String, Int>, vararg names: String): Int? {
        for (name in names) {
            headers[name]?.let { return it }
        }
        return null
    }

    private fun splitCsvLine(line: String): List<String> = line.split(",").map { it.trim() }

    private fun parseAssetClass(value: String): AssetClass = when (value.trim().uppercase()) {
        "BOND" -> AssetClass.BOND
        "ETF" -> AssetClass.ETF
        "REIT" -> AssetClass.REIT
        "CASH" -> AssetClass.CASH
        else -> AssetClass.STOCK
    }

    private fun guessAssetClass(symbol: String): AssetClass = when (symbol.uppercase()) {
        "BND", "AGG", "TLT", "IEF", "SHY", "LQD", "VCIT" -> AssetClass.BOND
        "VNQ", "O", "SPG", "PLD" -> AssetClass.REIT
        "VTI", "VOO", "SPY", "QQQ", "QQQM", "IWM", "VXUS", "SCHD", "VYM", "SCHG",
        "JEPI", "JEPQ", "AMDY", "SMCY" -> AssetClass.ETF
        else -> AssetClass.STOCK
    }

    private fun newId(): String = Random.nextLong().toString(16)
}
